import { BufferGeometry, DoubleSide, EdgesGeometry, Float32BufferAttribute, FrontSide, LineBasicMaterial, LineSegments, Material, Mesh, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, Object3D, ShaderMaterial, Vector2, Vector3 } from "three";
import { EMaterialSlot } from "./EMaterialSlot";
import { SubMesh } from "./SubMesh";
import { MeshInfo } from "./MeshInfo";
import { BufferGeometryUtils, LineMaterial, LineSegments2, LineSegmentsGeometry } from "three/examples/jsm/Addons.js";
import { OutsideEdgesGeometry } from "$src/infrastructure/designs/core/conditional-lines/OutsideEdgesGeometry";
import { ConditionalEdgesGeometry } from "$src/infrastructure/designs/core/conditional-lines/ConditionalEdgesGeometry";
import { ConditionalEdgesShader } from "$src/infrastructure/designs/core/conditional-lines/ConditionalEdgesShader";
import { ConditionalLineMaterial } from "$src/infrastructure/designs/core/conditional-lines/lines2/ConditionalLineMaterial";
import { ConditionalLineSegmentsGeometry } from "$src/infrastructure/designs/core/conditional-lines/lines2/ConditionalLineSegmentsGeometry";

var defaultMaterial = new MeshStandardMaterial({ color: 0xffffff, side: FrontSide }); 

export class SubMeshCollection {

    private subMeshes: { mesh: SubMesh; material: ISubMeshMaterial }[] = [];

    add(mesh: SubMesh, mat: EMaterialSlot): void;
    add(mesh: SubMesh, mat: Material): void;
    add(mesh: SubMesh, mat: ISubMeshMaterial): void;
    add(mesh: SubMesh, mat: any): void {
        if (mat instanceof Material) {
            this.subMeshes.push({ mesh, material: new FixedMaterial(mat) });
        } else if (Object.values(EMaterialSlot).includes(mat)) {
            this.subMeshes.push({ mesh, material: new MaterialSlot(mat) });
        } else {
            this.subMeshes.push({ mesh, material: mat });
        }
    }
    
    merge(collection: SubMeshCollection): void {
        if (collection && collection.subMeshes) 
            this.subMeshes.push(...collection.subMeshes);
    }

    groupByMaterial(): Map<ISubMeshMaterial, SubMesh[]> {
        const matGroups = new Map<ISubMeshMaterial, SubMesh[]>();

        for (const subMesh of this.subMeshes) {
            let foundKey: ISubMeshMaterial | undefined;

            for (const key of matGroups.keys()) {
                if (key.equals(subMesh.material)) {
                    foundKey = key;
                    break;
                }
            }

            if (!foundKey) {
                matGroups.set(subMesh.material, [subMesh.mesh]);
            } else {
                matGroups.get(foundKey)!.push(subMesh.mesh);
            }
        }

        return matGroups;
    }

    createMesh(parent: Object3D): MeshInfo {

        const geometry = new BufferGeometry();
        
        const vertices: Vector3[] = [];
        const uvs: Vector2[] = [];

        const subTriangles: { mat: ISubMeshMaterial, tris: number[] }[] = [];

        // Group submeshes by material
        const matGroups = this.groupByMaterial();
        
        const indices: number[]  = [];

        for (const [mat, meshes] of matGroups) {
            const subTriangle: number[] = [];
           
            for (const subMesh of meshes) {
                subTriangle.push(...subMesh.triangles.map(i => i + vertices.length));
                vertices.push(...subMesh.vertices);

                if (subMesh.uvs) {
                    uvs.push(...subMesh.uvs);
                }
            }
            indices.push(...subTriangle);
            subTriangles.push({ mat, tris: subTriangle });
        }

        const meshInfo = new MeshInfo();
        
        const mesh = new Mesh();
        meshInfo.mesh = mesh;

        geometry.setFromPoints(vertices);
        geometry.setIndex(indices);
        //geometry.setAttribute('uv', new Float32BufferAttribute(uvs.flatMap(uv => [uv.x, uv.y]), 2));
    
        geometry.clearGroups();
        const materials: Material[] = [];

        let groupStart = 0;

        subTriangles.forEach(({ mat, tris }, i) => {
            geometry.addGroup(groupStart, tris.length, i);
            groupStart += tris.length;

            if (mat instanceof MaterialSlot) {
                meshInfo.subMeshMap.set(mat.Slot, i);
                materials[i] = defaultMaterial;
            } else if (mat instanceof FixedMaterial) {
                materials[i] = mat.Material;
            }
        });

        mesh.material = materials;
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        geometry.computeVertexNormals();
        //geometry.computeTangents();
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        mesh.geometry = geometry;
      
        parent.add(meshInfo.mesh);


        const lineGeometry = new EdgesGeometry( geometry ); 
        // const line = new LineSegments(lineGeometry, new LineBasicMaterial( { color: 0x000000 } ) ); 
        // parent.add( line );

        // const mergeGeom = mesh.geometry.clone();
        // mergeGeom.deleteAttribute( 'uv' );
        // mergeGeom.deleteAttribute( 'uv2' );
        // const lineGeometry2 = new OutsideEdgesGeometry( BufferGeometryUtils.mergeVertices( mergeGeom, 1e-3 ) );
        // const line2 = new LineSegments(lineGeometry2, new LineBasicMaterial( { color: 0x000000 } ) ); 
        // //parent.add( line2 );

        const thickLineGeom = new LineSegmentsGeometry().fromEdgesGeometry( lineGeometry );
        const thickLines = new LineSegments2( thickLineGeom, new LineMaterial( { color:  0x000000, linewidth: 0.002 } ) );
        parent.add( thickLines );
        

        // const lineGeom = new ConditionalEdgesGeometry( BufferGeometryUtils.mergeVertices( mergeGeom ) );
		// 			const material = new ShaderMaterial( ConditionalEdgesShader );
		// 			material.uniforms.diffuse.value.set( 0xffff00 );

        // const line3 = new LineSegments( lineGeom, material );
        // parent.add( line3 );

        // const thickLineGeom1 = new ConditionalLineSegmentsGeometry().fromConditionalEdgesGeometry( lineGeom );
        // const thickLines1 = new LineSegments2( thickLineGeom1, new ConditionalLineMaterial( { color: 0x000000, linewidth: 2 } ) );
					
        // parent.add( thickLines1 );

        return meshInfo;
    }
}

export interface ISubMeshMaterial {
    equals(obj: any): boolean;
}

export class MaterialSlot implements ISubMeshMaterial {
    constructor(public Slot: EMaterialSlot) {}
    
    equals(obj: any): boolean {
        return obj instanceof MaterialSlot && this.Slot === obj.Slot;
    }
}

export class FixedMaterial implements ISubMeshMaterial {
    constructor(public Material: Material) {}

    equals(obj: any): boolean {
        return obj instanceof FixedMaterial && this.Material === obj.Material;
    }
}



class EdgeShader {

    static vertexShader = `
      varying vec2 vUv;
      void main()	{
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
      }
    `;
  
    static fragmentShader = `
          //#extension GL_OES_standard_derivatives : enable
      
      varying vec2 vUv;
      uniform float thickness;
         
      float edgeFactor(vec2 p){
          vec2 grid = abs(fract(p - 0.5) - 0.5) / fwidth(p) / thickness;
            return min(grid.x, grid.y);
      }
      
      void main() {
              
        float a = edgeFactor(vUv);
        
        vec3 c = mix(vec3(1), vec3(0), a);
        
        gl_FragColor = vec4(c, 1.0);
      }
    `;
  
    static material: ShaderMaterial = new ShaderMaterial({
      uniforms: {
          thickness: {
              value: 1.5
          }
        },
        vertexShader: EdgeShader.vertexShader,
        fragmentShader: EdgeShader.fragmentShader
    })
  }